/**
 * Profile Photos Section Component
 * Displays user's photo gallery with management options
 */
import { useState } from 'react';
import { Camera, Image } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

// Photo upload redirect functionality - inline implementation
const usePhotoUploadRedirect = () => {
  const navigate = useNavigate();
  
  const redirectToProfilePhotos = () => {
    console.log('🔄 Redirecting to Profile Photos section...');
    
    // Store the active section in localStorage so Profile page can read it
    localStorage.setItem('profileActiveSection', 'photos');
    localStorage.setItem('profileAutoEdit', 'true');
    
    // Navigate to profile page
    navigate('/profile');
  };
  
  return { redirectToProfilePhotos };
};

interface Photo {
  id: string;
  src: string;
  fileName: string;
  fileSize: number;
  uploadDate: string;
  isProfileDP: boolean;
}

interface ProfilePhotosSectionProps {
  userPhotos: Photo[];
  photosLoading: boolean;
  navigate: (path: string) => void;
}

export default function ProfilePhotosSection({ userPhotos, photosLoading, navigate }: ProfilePhotosSectionProps) {
  const [activePhoto, setActivePhoto] = useState(0);
  const { redirectToProfilePhotos } = usePhotoUploadRedirect();

  return (
    <Card className="border-2 border-gray-200 shadow-xl bg-white/80 backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-slate-800">
          <Image className="w-5 h-5 mr-2 text-blue-500" />
          My Photos
        </CardTitle>
        <CardDescription>
          {userPhotos.length} photos uploaded
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Photo Grid Status */}
        {photosLoading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-slate-600 text-sm">Loading your photos...</p>
          </div>
        ) : userPhotos.length === 0 ? (
          <div className="text-center py-8">
            <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 mb-4">No photos uploaded yet</p>
            <Button 
              onClick={redirectToProfilePhotos}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
            >
              <Camera className="w-4 h-4 mr-2" />
              Upload Your First Photo
            </Button>
          </div>
        ) : (
          <>
            {/* Square Photo Grid */}
            <div className="grid grid-cols-3 gap-3">
              {userPhotos.map((photo, index) => (
                <div 
                  key={photo.id}
                  className={`aspect-square rounded-xl cursor-pointer transition-all duration-300 ${
                    index === activePhoto 
                      ? 'ring-4 ring-blue-500 ring-offset-2 scale-105' 
                      : 'hover:scale-105'
                  }`}
                  onClick={() => setActivePhoto(index)}
                >
                  <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg overflow-hidden relative">
                    <img 
                      src={photo.src} 
                      alt={`Profile ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.log('❌ Photo failed to load from database:', photo.src);
                        console.log('Photo details:', photo);
                        e.currentTarget.src = 'https://via.placeholder.com/100x100?text=Photo';
                      }}
                      onLoad={() => console.log('✅ Photo loaded successfully:', photo.src)}
                    />
                    {photo.isProfileDP && (
                      <div className="absolute top-1 right-1">
                        <Badge className="bg-blue-500 text-white text-xs px-1 py-0">
                          DP
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Add Photo Button - Show if less than 10 photos */}
              {userPhotos.length < 10 && (
                <button 
                  onClick={redirectToProfilePhotos}
                  className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 flex flex-col items-center justify-center text-slate-500 hover:text-blue-500"
                >
                  <Camera className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium">Add Photo</span>
                </button>
              )}
            </div>

            {/* Photo Navigation Info */}
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <div className="text-sm">
                <span className="text-slate-600 font-medium">
                  Photo {activePhoto + 1} of {userPhotos.length}
                </span>
                {userPhotos[activePhoto] && (
                  <p className="text-xs text-slate-500 mt-1">
                    {userPhotos[activePhoto].fileName}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Removed duplicate "Add More Photos" button - using only the square "Add Photo" box in grid */}
      </CardContent>
    </Card>
  );
}